import { useState } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import {
  ArrowUturnDownIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { Card } from '../../../components/base/Card';
import { DataTable } from '../../../components/base/Table';
import { Button } from '../../../components/base/Button';
import { ConfirmDelete } from '../../../components/shared/ConfirmDelete';
import { LoadingWheel } from '../../../components/base/LoadingWheel';
import { useSuperAdminApplianceLookups } from '../../../hooks/useSuperAdminHardwareLookups';
import { useClipboardCopy } from '../../../hooks/useClipboardCopy';
import { Pill } from '../../../components/base/Pill';
import { IApplianceManufacturer } from '../../../types/Appliance/ApplianceManufacturer';
import { applianceQueryKeys } from '../../../data/QueryKeys';
import useApplianceManufacturerMutations from '../../../data/appliance/useApplianceManufacturerMutations';
import { CreateEditApplianceManufacturer } from '../../../components/Appliance/CreateEditApplianceManufacturer';

const colHelper = createColumnHelper<IApplianceManufacturer>();

export function ApplianceManufacturersPage() {
  const { CopyToClipboardButton } = useClipboardCopy();
  const { delApplianceManufacturer, updateApplianceManufacturer } =
    useApplianceManufacturerMutations();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editApplianceManufacturer, setEditApplianceManufacturer] = useState<
    IApplianceManufacturer | undefined
  >(undefined);
  const [deleteApplianceManufacturer, setDeleteApplianceManufacturer] =
    useState<IApplianceManufacturer | undefined>(undefined);
  const { isLoading, typeLookup } = useSuperAdminApplianceLookups();

  const handleDeleteHardwareManufacturer = (
    hardwareManufacturer: IApplianceManufacturer
  ) => {
    setDeleteApplianceManufacturer(hardwareManufacturer);
    setDeleteOpen(true);
  };

  const handleConfirmDeleteHardwareManufacturer = () => {
    delApplianceManufacturer.mutate(deleteApplianceManufacturer!);
    setDeleteOpen(false);
  };

  const handleRestoreHardwareManufacturer = (
    hardwareManufacturer: IApplianceManufacturer
  ) => {
    updateApplianceManufacturer.mutate({
      ...hardwareManufacturer,
      restore: true,
    });
  };

  const handleEditHardwareManufacturer = (
    hardwareManufacturer: IApplianceManufacturer
  ) => {
    setEditApplianceManufacturer(hardwareManufacturer);
    setEditOpen(true);
  };

  const handleCreateEditClose = () => {
    editApplianceManufacturer &&
      setTimeout(() => setEditApplianceManufacturer(undefined), 200);
    setEditOpen(false);
  };

  const cols = [
    colHelper.display({
      header: 'Status',
      cell: (info) =>
        info.row.original.disabled ? (
          <XMarkIcon className="w-5 h-5 opacity-70" />
        ) : (
          <CheckIcon className="w-5 h-5 opacity-70" />
        ),
    }),
    colHelper.accessor('id', { header: 'Id' }),
    colHelper.accessor(
      (row) =>
        row.hardwareTypeIds.map((type) => typeLookup(type)?.name).join(', '),
      {
        header: 'Type',
        cell: (info) => (
          <>
            {info.row.original.hardwareTypeIds.map((ht) => (
              <Pill>{typeLookup(ht)?.name}</Pill>
            ))}
          </>
        ),
      }
    ),
    colHelper.accessor('name', { header: 'Manufacturer' }),
    colHelper.display({
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center space-x-4">
          {info.cell.row.original.deletedOn ? (
            <ArrowUturnDownIcon
              className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
              onClick={() =>
                handleRestoreHardwareManufacturer(info.row.original)
              }
            />
          ) : (
            <>
              <CopyToClipboardButton iconOnly copyText={info.row.original.id} />
              <PencilIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() =>
                  handleEditHardwareManufacturer(info.row.original)
                }
              />
              <TrashIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() =>
                  handleDeleteHardwareManufacturer(info.row.original)
                }
              />
            </>
          )}
        </div>
      ),
    }),
  ];

  return (
    <>
      <CreateEditApplianceManufacturer
        open={editOpen}
        onClose={handleCreateEditClose}
        applianceManufacturer={editApplianceManufacturer}
      />
      <ConfirmDelete
        itemName={deleteApplianceManufacturer?.name}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => handleConfirmDeleteHardwareManufacturer()}
      />
      <Card title="Appliance Manufacturers">
        <div className="my-6">
          <Button onClick={() => setEditOpen(true)}>Add Manufacturer</Button>
        </div>
        {isLoading ? (
          <LoadingWheel />
        ) : (
          <div>
            <DataTable<IApplianceManufacturer>
              url="/superadmin/hardware/manufacturers"
              queryKey={applianceQueryKeys.superAdminManufacturers()}
              columns={cols}
              enableShowDeleted
            />
          </div>
        )}
      </Card>
    </>
  );
}
